/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtomValue, useStateXSelector } from '@cloudio-saas/statex';
import { Box, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { useManager } from 'componentregistry';
import { Platform } from 'componentregistry/platform/Platform';
import { activeItemSelector } from 'designer/activeItemSelector';
import { activeItemIdsAtom, itemAtom } from 'designer/atoms';
import useDnD from 'designer/useDnD';
import { useIORef } from 'designer/useIORef';
import useThrowPromiseIfNull from 'hooks/useThrowPromiseIfNull';
import Icon from 'icons/Icon';
import { itemExpandedSelector } from 'layout/Sidebar/itemExpandedSelector';
import React, { memo, useEffect } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { inViewBox } from 'store/utils/utils';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { itemFilteredAtom } from '../../atoms';
import {
  LG,
  MD,
  SM,
  XL,
  XS,
} from '../PropertiesView/property-editors/ResponsiveProperty';
import TreeNodeExpandCollapseIcon from './TreeNodeExpandCollapseIcon';
import { isViewOnlyDeveloper } from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  node: {
    paddingLeft: 8,
    color: theme.palette.text.primary,
    cursor: 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    position: 'relative',
    display: 'block',
  },
  movable: {
    position: 'relative',
  },
  display: {
    color: theme.palette.text.secondary,
    marginLeft: '20px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.8rem',
  },
  leaf: {
    '& > $label': {
      paddingLeft: 4,
    },
  },
  icon: {
    lineHeight: '24px',
    padding: 0,
  },
  content: {
    padding: 0,
    marginLeft: 8,
    overflow: 'visible',
    '& $content': {},
    '& $node': {},
  },
  expander: {
    cursor: 'pointer',
    position: 'absolute',
    color: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
    top: 4.5,
    left: 0,
    zIndex: 1,
  },
  label: {
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    lineHeight: '24px',
    outline: 'none',
    height: '24px',
    // paddingLeft: 20,
  },
  labelInner: {
    display: 'flex',
    alignItems: 'center',
    outline: 'none',
  },
  expanderWrapper: {
    width: '16px',
    height: '100%',
    position: 'absolute',
    left: '-25px',
  },
  root: {},
  expanded: {
    '& > $child': {
      '&::after': {
        width: '1px',
        top: '50%',
        bottom: 0,
        left: '50%',
      },
    },
  },
  child: {
    width: '16px',
    position: 'absolute',
    height: '24px',
    left: 8,
    '&::before': {
      height: '1px',
      top: '50%',
      right: 0,
      left: 0,
    },
  },
  filtered: { color: theme.palette.secondary.dark },
  multiActive: {
    color: theme.palette.primary.light,
    '& > $label > &labelInner > $display': {
      marginLeft: 0,
    },
  },
  active: {
    color: theme.palette.primary.main,
    '& > $movable > $label > $labelInner > $display': {
      marginLeft: 0,
    },
    '& > $movable > $label > $labelInner > $svgIcon > svg': {
      fill: theme.palette.primary.main,
    },
  },
  activeCheck: { marginLeft: '4px', marginRight: '4px' },
  compIcon: {
    marginRight: '8px',
  },
  svgIcon: {
    marginLeft: '8px',
    '& > svg': {
      fill: theme.palette.text.secondary,
      width: '1em',
      height: '1em',
    },
  },
}));

interface Props {
  appUid: string;
  first: boolean;
  itemId: string;
  last: boolean;
  level: number;
  orphan: boolean;
  pageId: string;
  platform: Platform;
  root?: boolean;
  rootref?: React.RefObject<HTMLUListElement | null>;
  treename: string;
  onClick?: (
    e: React.SyntheticEvent<HTMLElement, MouseEvent>,
    itemId: string,
  ) => void;
  onDoubleClick?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    itemId: string,
  ) => void;
  onContextMenu?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    itemId: string,
  ) => void;
  dragging?: boolean;
}

function Tree({
  appUid,
  dragging = false,
  first,
  itemId = 'root',
  last,
  level,
  onClick,
  onContextMenu,
  onDoubleClick,
  orphan,
  pageId,
  platform,
  root,
  rootref,
  treename,
}: Props): React.ReactElement {
  const _item = useStateXAtomValue(itemAtom, {
    params: { appUid, pageId, itemId },
  });
  // _item will be undefined if the page is still not loaded. Happens when click on the logo
  // to navigate to the home page while the designer (tree view) is open
  const item = useThrowPromiseIfNull(_item);
  const mgr = useManager(item.type);
  const expanded = useStateXSelector(itemExpandedSelector, {
    params: { appUid, pageId, itemId, treename },
  });
  const ref = useIORef<HTMLDivElement>();
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);
  const movable = treename === 'designer';
  const { dragging: isDragging } = useDnD({
    appUid,
    pageId,
    item,
    ref,
    disabled: !movable || isViewOnlyUser,
  });
  const filtered = useStateXAtomValue(itemFilteredAtom, {
    params: { appUid, pageId, itemId, treename },
  });
  const { itemId: activeItemId } = useStateXSelector(activeItemSelector, {
    params: { appUid, treename, pageId },
    props: platform,
    shouldComponentUpdate: (value, oldValue) => {
      return value.itemId === itemId || oldValue?.itemId === itemId;
    },
  });
  const activeItemIds = useStateXAtomValue(activeItemIdsAtom, {
    params: { appUid, pageId },
    shouldComponentUpdate: (value, oldValue) => {
      return value.includes(itemId) || !!oldValue?.includes(itemId);
    },
  });
  const active = itemId === activeItemId;
  const multiActive = activeItemIds.includes(itemId);
  useEffect(() => {
    if (active && ref.current && rootref && !inViewBox(ref, rootref)) {
      const timeout = setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [active, item.itemId, item.type, ref, rootref]);

  const hasChildren = item.children?.length > 0;
  const classes = useStyles({ hasChildren });

  const className = clsx({
    [classes.node]: true,
    [classes.active]: active,
    [classes.multiActive]: multiActive,
    [classes.leaf]: !item.children?.length,
    [classes.expanded]: expanded,
    [classes.filtered]: filtered,
  });

  const childNodes = item.children?.map((c, index) => {
    return (
      <Tree
        appUid={appUid}
        dragging={dragging || isDragging}
        first={index === 0}
        itemId={c}
        key={c}
        last={index === item.children.length - 1}
        level={level + 1}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onDoubleClick={onDoubleClick}
        orphan={orphan}
        pageId={pageId}
        platform={platform}
        rootref={rootref}
        treename={treename}
      />
    );
  });

  const opacity = isDragging ? 0.2 : 1;

  const btn = hasChildren ? (
    <TreeNodeExpandCollapseIcon
      className={classes.expander}
      mgr={mgr}
      item={item}
      pageId={pageId}
      appUid={appUid}
      treename={treename}
    />
  ) : null;

  const _onClick = (e: React.SyntheticEvent<HTMLElement, MouseEvent>) => {
    if (onClick) {
      onClick(e, itemId);
    }
  };
  const _onDoubleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (onDoubleClick) {
      onDoubleClick(e, itemId);
    }
  };
  const _onContexMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (onContextMenu) {
      onContextMenu(e, itemId);
    }
  };

  if (!mgr) {
    return (
      <div
        style={{
          opacity,
          color: 'red',
        }}
        className={className}
      >
        {item.type} [Unknown!]
      </div>
    );
  }

  // const layoutId = `tn:${itemId}:${pageId}`;

  let icon = mgr.icon;
  if (typeof icon === 'function') {
    icon = icon(itemWithDefaultValues(item));
  }

  return (
    <li
      style={{
        opacity,
      }}
      className={className}
    >
      <div className={classes.movable}>
        {btn ? <div className={classes.expanderWrapper}>{btn}</div> : null}
        <div className={clsx(classes.label)}>
          <span
            data-testid={`tree-${itemId}`}
            className={classes.labelInner}
            onClick={_onClick}
            onKeyDown={(e) => clickOnEnterOrSpace(e, _onClick)}
            ref={ref}
            role="button"
            tabIndex={0}
            onContextMenu={_onContexMenu}
            onDoubleClick={_onDoubleClick}
            aria-label={mgr.name}
            title={mgr.name}
          >
            {icon?.iconName ? (
              <Icon
                {...icon}
                color={active ? 'primary' : undefined}
                className={classes.compIcon}
              />
            ) : null}
            {mgr.deprecated ? (
              <Icon
                iconName="exclamation-triangle"
                color="error"
                data-tip-error
                data-tip-delay={0}
                data-tip={`Deprecated: ${mgr.deprecated}`}
                sx={{ mr: 1 }}
              />
            ) : null}
            {mgr.name}
            {item.type !== mgr.type ? ` [Actual Type: ${item.type}] ` : ' '}
            {mgr.version ? `(v${mgr.version})` : null}
            {item.props.visible === false ? (
              <Icon
                iconName="eye-slash"
                color="error"
                data-tip="Visible Prop set to off"
                sx={{ ml: 1 }}
              />
            ) : null}

            {item.props.responsive?.xl ? (
              <Box className={classes.svgIcon}>
                <XL selected data-tip="Hidden on Extra Large devices" />
              </Box>
            ) : null}
            {item.props.responsive?.lg ? (
              <Box className={classes.svgIcon}>
                <LG selected data-tip="Hidden on Large devices" />
              </Box>
            ) : null}
            {item.props.responsive?.md ? (
              <Box className={classes.svgIcon}>
                <MD selected data-tip="Hidden on Medium devices" />
              </Box>
            ) : null}
            {item.props.responsive?.sm ? (
              <Box className={classes.svgIcon}>
                <SM selected data-tip="Hidden on Small devices" />
              </Box>
            ) : null}
            {item.props.responsive?.xs ? (
              <Box className={classes.svgIcon}>
                <XS selected data-tip="Hidden on Extra Small devices" />
              </Box>
            ) : null}
            {active ? (
              <Icon
                color="primary"
                iconName="check"
                className={classes.activeCheck}
              />
            ) : multiActive ? (
              <Icon iconName="check-double" className={classes.activeCheck} />
            ) : null}
            <span className={classes.display}>
              {mgr.getDisplayText(itemWithDefaultValues(item), expanded, {
                appUid,
                pageId,
                itemId,
                mgr,
                platform,
              })}
            </span>
          </span>
        </div>
      </div>
      {expanded && childNodes?.length ? (
        <ul className={classes.content}>{childNodes}</ul>
      ) : null}
    </li>
  );
}

export default memo(Tree);
